import React, { useState } from "react";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";

// Components
import Dashboard from "./components/Dashboard/Dashboard";
import Alert from "./components/UI/Alert";
import AddCategoryForm from "./components/Forms/AddCategoryForm";
import { Alert as AlertType } from "./types";
import Sidebar from "./components/Sidebar/Sidebar";
import CreateCategory from "./components/Forms/CreateCategory";
import CategoryTable from "./screens/CategoryScreen";
import SubCategoryTable from "./screens/SubCategoryScreen";
import SubCreateCategory from "./components/Forms/CreateSubCategory";
import ApiToggle from "./components/UI/ApiToggle";
import UserManagementScreen from "./screens/UserManagementScreen";
import UserDetailsScreen from "./screens/UserDetails/UserDetailsScreen";
import LoginScreen from "./screens/LoginScreen";
import ProtectedRoute from "./components/ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import TemplateListScreen from "./screens/TemplateListScreen";
import TemplateDetailScreen from "./screens/TemplateDetailScreen";
import AddPSDTemplateForm from "./components/Forms/AddPSDTemplateForm";
import { isUsersOnlyAdmin } from "./config/roles";
import BillingScreen from "./screens/Billing/BillingScreen";
import ProductSwitcher from "./components/UI/ProductSwitcher";
import { useProduct } from "./context/ProductContext";
import { hasSection, homePath } from "./config/products";

const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, login, userEmail } = useAuth();
  const usersOnly = isUsersOnlyAdmin(userEmail);
  const { product } = useProduct();
  const showTemplates = hasSection(product, "templates");
  const home = homePath(product);

  // Helper function to determine active tab based on pathname
  const getActiveTab = (pathname: string): number => {
    if (pathname === "/billing" || pathname.startsWith("/billing/")) {
      return 4; // Billing
    } else if (pathname === "/") {
      return 0; // Dashboard
    } else if (pathname === "/categories/new" || pathname === "/psd-template/new" || pathname.includes("/new")) {
      return 1; // Add Template
    } else if (
      pathname === "/category" ||
      pathname === "/createCategory" ||
      pathname.startsWith("/sub-category/") ||
      pathname.startsWith("/sub-createCategory/") ||
      pathname.startsWith("/categories/") ||
      pathname.startsWith("/template/") ||
      pathname.includes("/templates")
    ) {
      return 2; // Category Management
    } else if (pathname === "/users" || pathname.startsWith("/users/")) {
      return 3; // User Management
    } else {
      return 0; // fallback to Dashboard
    }
  };

  const activeTab = getActiveTab(location.pathname);

  const [alert, setAlert] = useState<AlertType>({
    show: false,
    message: "",
    type: "success",
  });

  const handleTabChange = (tab: number) => {
    if (tab === 0) navigate("/");
    else if (tab === 1) navigate("/categories/new");
    else if (tab === 2) navigate("/category");
    else if (tab === 3) navigate("/users");
    else if (tab === 4) navigate("/billing");
  };

  const handleCloseAlert = () => {
    setAlert({ show: false, message: "", type: "success" });
  };

  return (
    <Routes>
      {/* Login Route */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            <Navigate to={home} replace />
          ) : (
            <LoginScreen onLogin={login} />
          )
        }
      />

      {/* Protected Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <div className="h-screen flex overflow-hidden bg-gray-50">
              {/* Sidebar - fixed */}
              <div className="w-72 flex-shrink-0">
                <Sidebar activeTab={activeTab} onTabChange={handleTabChange} />
              </div>

              {/* Main Content */}
              <div className="flex-1 flex flex-col min-w-0">
                {/* Top Bar - fixed */}
                <div className="h-16 bg-gradient-to-r from-white to-gray-50 shadow-md border-b border-gray-200 flex items-center px-6 sticky top-0 z-10 backdrop-blur-sm bg-white/95">
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-1 bg-gradient-to-b from-indigo-600 to-purple-600 rounded-full"></div>
                      <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                        {(() => {
                          if (activeTab === 0) return "Dashboard Overview";
                          else if (activeTab === 1) return "Add New Template";
                          else if (activeTab === 2)
                            return "Category Management";
                          else if (activeTab === 3) return "User Management";
                          else if (activeTab === 4) return "Billing";
                          else return "Dashboard";
                        })()}
                      </h2>
                    </div>
                    <div className="flex items-center gap-6">
                      <ProductSwitcher />
                      <ApiToggle />
                      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <div className="text-sm text-gray-700 font-medium">
                          {new Date().toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Alert */}
                <Alert alert={alert} onClose={handleCloseAlert} />

                {/* Routed content - scrollable area */}
                <div className="flex-1 overflow-y-auto">
                  {usersOnly ? (
                    <Routes>
                      <Route
                        path="/users"
                        element={<UserManagementScreen />}
                      />
                      <Route path="/users/:userId" element={<UserDetailsScreen />} />
                      {/* Restricted admins: every other route redirects to Users */}
                      <Route
                        path="*"
                        element={<Navigate to="/users" replace />}
                      />
                    </Routes>
                  ) : (
                    <Routes>
                      <Route
                        path="/"
                        element={
                          hasSection(product, "dashboard") ? <Dashboard /> : <Navigate to={home} replace />
                        }
                      />
                      {showTemplates && (
                        <>
                          <Route
                            path="/categories/new"
                            element={<AddPSDTemplateForm />}
                          />
                          <Route
                            path="/psd-template/new"
                            element={<AddPSDTemplateForm />}
                          />
                          <Route
                            path="/categories/:categoryId/:categoryName/subcategories/:subCategoryId/:subCategoryName/new"
                            element={<AddCategoryForm />}
                          />
                          <Route
                            path="/categories/:categoryId/:categoryName/subcategories/:subCategoryId/:subCategoryName/templates"
                            element={<TemplateListScreen />}
                          />
                          <Route
                            path="/template/:templateId"
                            element={<TemplateDetailScreen />}
                          />
                          <Route path="/category" element={<CategoryTable />} />
                          <Route
                            path="/sub-category/:categoryId"
                            element={<SubCategoryTable />}
                          />
                          <Route
                            path="/createCategory"
                            element={<CreateCategory />}
                          />
                          <Route
                            path="/sub-createCategory/:categoryId"
                            element={<SubCreateCategory />}
                          />
                        </>
                      )}
                      <Route path="/users" element={<UserManagementScreen />} />
                      <Route
                        path="/users/:userId"
                        element={
                          hasSection(product, "userDetails") ? (
                            <UserDetailsScreen />
                          ) : (
                            <Navigate to="/users" replace />
                          )
                        }
                      />
                      <Route path="/billing" element={<BillingScreen />} />
                      {/* Screens the selected product doesn't have land on its home screen */}
                      <Route path="*" element={<Navigate to={home} replace />} />
                    </Routes>
                  )}
                </div>
              </div>
            </div>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default App;
